import { SimpleFlex } from '@eversports/react-components/primitives/Flex'
import { SimpleText } from '@eversports/react-components/primitives/Text'
import { assertNever } from '@eversports/utility-types'
import React from 'react'

import { ConfirmWithOption, getRepeatingCycle, splitUsagesByActivityScope } from '../../typings/Product'

import { AssignedProductAdditionalInformation } from './AssignedProductAdditionalInformation'
import AssignedProductUsageInformation from './AssignedProductUsageInformation'
import { UsageLimitPeriodInformation } from './UsageLimitPeriodInformation'

interface Props {
  assignedProduct: ConfirmWithOption
}

const AssignedProductName: React.FC<React.PropsWithChildren<Props>> = ({ assignedProduct }) => {
  const productValidity = assignedProduct.assignedProductValidity

  switch (assignedProduct.__typename) {
    case 'AssignedMembership': {
      // TODO: tackle this once we have a validity for the memebership on the schema
      // const hasAutoExtension = Boolean(assignedProduct.product.runtime.renewal)
      // // TODO: make this have the actual extension date
      // const membershipInformation = hasAutoExtension ? (
      //   <Localized id="assigned-product-auto-extension-date" params={{ extensionDate: new Date('2020-03-31') }} />
      // ) : (
      //   // TODO make this have the actual valid until date
      //   <Localized
      //     id="assigned-product-valid-until"
      //     params={{ validUntil: assignedProduct.assignedProductValidity.usageLimitPeriod!.end }}
      //   />
      // )
      //
      const { usageLimitPeriod } = assignedProduct.assignedProductValidity
      const { activityUsages, overallUsages } = splitUsagesByActivityScope(assignedProduct.product.usages)
      const repeating = getRepeatingCycle(activityUsages, overallUsages)
      const validityDuration = assignedProduct.product.runtime.renewal ? null : assignedProduct.product.runtime.initial

      return (
        <SimpleFlex flexDirection="column">
          <SimpleText sx={{ fontWeight: 'bold' }} data-testid="assigned-membership-option">
            {assignedProduct.product.name}
          </SimpleText>
          <SimpleText sx={{ color: 'subtleText' }}>
            {productValidity.isValid ? (
              usageLimitPeriod && (
                <UsageLimitPeriodInformation
                  usageLimitPeriod={usageLimitPeriod}
                  repeating={repeating}
                  validityDuration={validityDuration}
                />
              )
            ) : (
              <AssignedProductAdditionalInformation
                additionalInformation={productValidity.additionalInformation}
                usageLimitPeriod={usageLimitPeriod}
                repeating={repeating}
                validityDuration={validityDuration}
              />
            )}
          </SimpleText>
        </SimpleFlex>
      )
    }
    case 'AssignedCard': {
      const cardUsages = splitUsagesByActivityScope(assignedProduct.product.usages)
      const cardRepeating = getRepeatingCycle(cardUsages.activityUsages, cardUsages.overallUsages)
      const cardValidityDuration = assignedProduct.product.validity?.duration ?? null

      return (
        <SimpleFlex flexDirection="column">
          <SimpleText sx={{ fontWeight: 'bold' }} data-testid="assigned-card-option">
            {assignedProduct.product.name}
          </SimpleText>
          <SimpleText sx={{ color: 'subtleText' }}>
            {productValidity.isValid ? (
              <AssignedProductUsageInformation
                validUntil={assignedProduct.validUntil}
                usageLimitPeriod={productValidity.usageLimitPeriod}
                usageLimitTotal={productValidity.usageLimitTotal}
                repeating={cardRepeating}
                validityDuration={cardValidityDuration}
              />
            ) : (
              <AssignedProductAdditionalInformation
                additionalInformation={productValidity.additionalInformation}
                usageLimitPeriod={productValidity.usageLimitPeriod}
                repeating={cardRepeating}
                validityDuration={cardValidityDuration}
              />
            )}
          </SimpleText>
        </SimpleFlex>
      )
    }
    default:
      return assertNever(assignedProduct)
  }
}

export default AssignedProductName
